import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import Logo from "./Logo";
import {
  LayoutDashboard,
  BarChart3,
  MessageSquare,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Settings2,
  Bot,
  ChevronDown,
  Cloud,
  Wrench,
  Plug,
  Upload,
  Activity,
  User,
  Menu,
  X,
} from "lucide-react";

const mainNavItems = [
  { to: "/", label: "Ops Agent", icon: MessageSquare, end: true },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/analysis", label: "Data Analysis", icon: BarChart3 },
  { to: "/settings?tab=models", label: "Settings", icon: Settings2 },
];

const settingsSubLinks = [
  { to: "/settings?tab=models", label: "Models API", icon: Cloud },
  { to: "/settings?tab=skills", label: "Skills", icon: Wrench },
  { to: "/settings?tab=agents", label: "Agents", icon: Bot },
  { to: "/settings?tab=tools", label: "Tools", icon: Plug },
  { to: "/settings?tab=upload", label: "Upload Reports", icon: Upload },
  { to: "/settings?tab=tokens", label: "Token Usage", icon: Activity },
  { to: "/settings?tab=profile", label: "Profile", icon: User },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [version, setVersion] = useState<any>(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.innerWidth < 1024 ? true : localStorage.getItem("sidebar-collapsed") === "true"; } catch { return true; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("sidebar-collapsed", String(collapsed)); } catch {}
  }, [collapsed]);

  useEffect(() => {
    fetch("/api/version")
      .then((r) => r.json())
      .then(setVersion)
      .catch(() => {});
  }, []);

  const isSettingsActive = location.pathname === "/settings";

  const NavItem = ({ item, onClick }: { item: any; onClick?: () => void }) => (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
          collapsed ? "justify-center" : ""
        } ${
          isActive
            ? "bg-novacare-700 text-white shadow-sm"
            : "text-novacare-100 hover:text-white hover:bg-novacare-700/50"
        }`
      }
    >
      <item.icon className="w-5 h-5 shrink-0" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );

  const SidebarContent = ({ closeMobile }: { closeMobile?: () => void }) => (
    <>
      {/* Logo + collapse toggle */}
      <div className={`px-3 py-3 border-b border-novacare-900/30 flex items-center ${collapsed ? "flex-col justify-center gap-2" : "gap-2"}`}>
        {!collapsed ? (
          <div className="bg-white rounded-lg p-2 flex-1">
            <Logo size="md" />
          </div>
        ) : (
          <div className="bg-white rounded-lg p-2">
            <Logo size="sm" compact />
          </div>
        )}
        {!mobileOpen && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 text-novacare-200 hover:text-white hover:bg-novacare-700/50 rounded-lg shrink-0"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
        )}
      </div>

      {!collapsed && (
        <p className="text-xs text-novacare-200 px-5 py-1 tracking-wide">Healthcare Analytics</p>
      )}

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
        {mainNavItems.map((item) => (
          <NavItem key={item.to} item={item} onClick={closeMobile} />
        ))}

        {/* Settings group — icons-only when collapsed */}
        {collapsed && (
          <div className="pt-2">
            <div className="flex flex-col items-center gap-1">
              {settingsSubLinks.map((item) => {
                const active = location.pathname === "/settings" && location.search.includes(item.to.split("?")[1]);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={item.label}
                    onClick={closeMobile}
                    className={`p-2 rounded-lg transition-colors ${
                      active
                        ? "bg-novacare-700 text-white"
                        : "text-novacare-200 hover:text-white hover:bg-novacare-700/50"
                    }`}
                  >
                    <item.icon className="w-4 h-4 shrink-0" />
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* User + logout */}
      <div className="px-2 py-3 border-t border-novacare-900/30">
        <button
          onClick={() => { navigate("/settings?tab=profile"); closeMobile?.(); }}
          className={`w-full flex items-center gap-3 mb-2 rounded-xl hover:bg-novacare-700/40 transition-colors p-1 ${collapsed ? "justify-center" : "px-2 py-1"}`}
          title={collapsed ? user?.name : "Go to Profile"}
        >
          {user?.photo_url ? (
            <img
              src={user.photo_url}
              alt={user?.name || "User"}
              className="w-9 h-9 rounded-full object-cover border border-novacare-600 shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-novacare-700 flex items-center justify-center text-white text-sm font-medium border border-novacare-600 shrink-0">
              {user?.name ? user.name[0].toUpperCase() : "?"}
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0 text-left">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-novacare-300 truncate">{user?.role || "Automation & AI Specialist"}</p>
            </div>
          )}
        </button>
        <button
          onClick={logout}
          title={collapsed ? "Logout" : undefined}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-novacare-100 hover:text-white hover:bg-novacare-700/50 transition-colors w-full ${
            collapsed ? "justify-center" : ""
          }`}
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>

      {/* Version footer (only when expanded) */}
      {!collapsed && version && (
        <div className="px-4 py-2 border-t border-novacare-900/30 text-xs text-novacare-300">
          <p>v{version.version} / {version.upload_count} uploads</p>
        </div>
      )}
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex ${collapsed ? "w-20" : "w-64"} bg-novacare-800 flex-col shrink-0 transition-all duration-200`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-novacare-800 z-40 flex items-center justify-between px-3 border-b border-novacare-900/30">
        <div className="flex items-center gap-2">
          <div className="bg-white rounded-lg p-1.5">
            <Logo size="sm" />
          </div>
          <span className="text-white font-medium text-sm">Healthcare Analytics</span>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-novacare-100 hover:text-white hover:bg-novacare-700/50 rounded-lg"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-30 flex">
          <div className="w-64 bg-novacare-800 flex flex-col pt-14 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <SidebarContent closeMobile={() => setMobileOpen(false)} />
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto lg:pt-0 pt-14">
        <Outlet />
      </main>
    </div>
  );
}
